import React, { useMemo } from 'react';

import TimeSeriesChart from '../../chart/echarts/TimeSeriesChart';
import { EChartPoint, EChartTimeSeries } from '../../chart/echarts/types';
import { getConvertedTimeToUTC } from "../../../helpers/utils";
import { CHART_COLOURS_STATEMENTS, CHART_DATA_TYPE } from '../../Constants';

// Re-use types from GroupDetailSlideout
interface TimeSeriesDataPoint {
    timeslice: string; // ISO timestamp string
    value: number;
}

interface InstanceSeriesData {
    instanceId: number;
    instanceName: string;
    data: TimeSeriesDataPoint[];
}

interface MultiInstanceTimeSeriesChartProps {
    seriesData: InstanceSeriesData[];
    yAxisLabel: string;
    valueSuffix?: string;
    chartType?: 'spline' | 'line' | 'area' | 'column';
}

const MultiInstanceTimeSeriesChart: React.FC<MultiInstanceTimeSeriesChartProps> = ({
    seriesData,
    yAxisLabel,
    valueSuffix = '',
    chartType = 'spline',
}) => {
    const isTime = valueSuffix === 'ms';

    const axis = useMemo(() => [{
        labels: true as const,
        name: yAxisLabel,
        format: isTime ? 'time' as const : 'number' as const,
    }], [yAxisLabel, isTime]);

    const series = useMemo<EChartTimeSeries[]>(() => {
        const total = (entry: typeof seriesData[number]) =>
            entry.data.reduce((sum, point) => sum + (point.value || 0), 0);

        return [...seriesData].sort((first, second) => total(second) - total(first))
            .map((instanceSeries, index) => ({
        name: instanceSeries.instanceName,
        chartType: chartType === 'column' ? 'bar' : 'line',
        color: CHART_COLOURS_STATEMENTS[index % CHART_COLOURS_STATEMENTS.length],
        data: instanceSeries.data.map((point): EChartPoint => [getConvertedTimeToUTC(point), point.value]),
                smooth: chartType === 'spline',
            }));
    }, [seriesData, chartType]);

    return (
        <div>
            <TimeSeriesChart series={series}
                             tooltipType={isTime ? CHART_DATA_TYPE.TIME : CHART_DATA_TYPE.GENERIC}
                             legend={true}
                             yAxis={axis}/>
        </div>
    );
};

export default MultiInstanceTimeSeriesChart;
